import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import icons from "./icons";
import artifactIcons from "./kanaiArtifactIcons";
import { createArtifact } from "./kanaiArtifactsApi";
import { TITLE_MAX_LENGTH } from "./saveAnswerHelpers";
import { getProjects } from "../../api";
import useFokusfalla from "./hooks/useFokusfalla";

/* ================================================================
   SaveAnswerModal (LF-5) — sparar ett KanAI-svar som markdown-dokument
   i projektets mapp "KanAI-svar". Återanvänder KanAI:s modalklasser
   (kai-modal-*) ur kanaiCss.js; tillägg ligger i style-blocket nedan,
   scopat med prefixet kanai-save-*. Style-blocket renderas alltid
   (även när modalen är stängd) eftersom SaveAnswerAction och
   "Sparat"-raden delar det. Lila accent: AI-funktion.
   ================================================================ */
const PREVIEW_LINES = 6;

const css = `
/* Självförsörjande grundstilar (2026-10-15): modalen och spara-knappen
   används även i hörn-KanAI på andra sidor, där KanAI-sidans kanaiCss.js
   (kai-modal-*, kai-msg-action-btn) inte är laddad. Utan dessa renderades
   dialogen ostylad i sidan och ikonen i full storlek. Samma värden som i
   kanaiCss.js; scopade så att KanAI-sidan inte påverkas. */
.kanai-save-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  z-index: 600; display: flex; align-items: center; justify-content: center;
}
.kanai-save-overlay .kai-modal {
  width: 520px; max-width: calc(100vw - 32px); background: var(--kp-white, #fff);
  border-radius: 14px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); overflow: hidden;
  font-family: inherit; color: var(--kp-text, #1E293B);
  /* Låg skärm (stagekontroll 2026-10-16: 792 px dialog i 720 px fönster):
     rubrik och knappar står kvar, bara innehållet rullar. */
  display: flex; flex-direction: column; max-height: calc(100vh - 32px);
}
.kanai-save-overlay .kai-modal-header, .kanai-save-overlay .kai-modal-footer { flex-shrink: 0; }
.kanai-save-overlay .kai-modal-header {
  padding: 16px 20px; border-bottom: 1px solid var(--kp-border, #E2E8F0);
  display: flex; align-items: center; justify-content: space-between;
}
.kanai-save-overlay .kai-modal-title { font-size: 15px; font-weight: 600; color: var(--kp-text, #1E293B); }
.kanai-save-overlay .kai-modal-close {
  width: 28px; height: 28px; border: none; background: none; cursor: pointer;
  color: var(--kp-text-muted, #7E8FA3); border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
}
.kanai-save-overlay .kai-modal-close:hover { background: var(--kp-bg, #F1F3F6); color: var(--kp-text, #1E293B); }
.kanai-save-overlay .kai-modal-close svg { width: 14px; height: 14px; }
.kanai-save-overlay .kai-modal-body { padding: 16px 20px; overflow-y: auto; min-height: 0; flex: 1 1 auto; }
.kanai-save-overlay .kai-modal-label {
  font-size: 12px; font-weight: 600; color: var(--kp-text-secondary, #64748B);
  margin-bottom: 6px; display: block;
}
.kanai-save-overlay .kai-modal-input {
  width: 100%; padding: 8px 12px; font-size: 13px; font-family: inherit;
  background: var(--kp-inset, #F0F2F5); border: 1px solid transparent;
  border-radius: 10px; color: var(--kp-text, #1E293B); outline: none; box-sizing: border-box;
}
.kanai-save-overlay .kai-modal-input:focus {
  background: var(--kp-white, #fff); border-color: var(--kp-purple, #7C3AED);
  box-shadow: 0 0 0 3px rgba(124,58,237,0.1);
}
.kanai-save-overlay .kai-modal-footer {
  padding: 12px 20px; border-top: 1px solid var(--kp-border, #E2E8F0);
  display: flex; justify-content: flex-end; gap: 8px;
}
.kanai-save-overlay .kai-modal-btn {
  padding: 7px 16px; border: 1px solid var(--kp-border, #E2E8F0);
  background: var(--kp-white, #fff); border-radius: 6px;
  font-size: 13px; font-weight: 500; color: var(--kp-text-body, #475569);
  cursor: pointer; font-family: inherit;
}
.kanai-save-overlay .kai-modal-btn:hover { background: var(--kp-bg, #F1F3F6); color: var(--kp-text, #1E293B); }
.kanai-save-overlay .kai-modal-btn.primary { background: var(--kp-purple-fill, #7C3AED); color: var(--kp-on-fill, #fff); border-color: var(--kp-purple-fill, #7C3AED); }
.kanai-save-overlay .kai-modal-btn.primary:hover { background: var(--kp-purple-fill-hover, #6D28D9); }
.kanai-save-overlay .kai-modal-btn.primary:disabled { opacity: 0.5; cursor: default; }
.kanai-save-btn {
  padding: 4px 8px; border: none; background: none; color: var(--kp-text-muted, #7E8FA3);
  font-size: 11px; font-family: inherit; cursor: pointer; border-radius: 4px;
  display: inline-flex; align-items: center; gap: 4px;
}
.kanai-save-btn:hover { background: var(--kp-bg, #F1F3F6); color: var(--kp-text, #1E293B); }
.kanai-save-btn svg { width: 12px; height: 12px; flex-shrink: 0; }
.kanai-save-field { margin-bottom: 14px; }
.kanai-save-projects { margin-top: 6px; border: 1px solid var(--kp-border, #E5E7EB); border-radius: 8px; max-height: 176px; overflow-y: auto; background: var(--kp-white, #FFFFFF); }
.kanai-save-project { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 10px; border: 0; border-bottom: 1px solid var(--kp-border-subtle, var(--kp-border, #E5E7EB)); background: transparent; color: var(--kp-text, #1E293B); font: inherit; font-size: 12.5px; text-align: left; cursor: pointer; }
.kanai-save-project:last-child { border-bottom: 0; }
.kanai-save-project:hover, .kanai-save-project.is-selected { background: var(--kp-inset, #F3F4F6); }
.kanai-save-project-nr { font-weight: 600; color: var(--kp-text-muted, #7E8FA3); min-width: 64px; }
.kanai-save-project-empty { padding: 10px; font-size: 12px; color: var(--kp-text-muted, #7E8FA3); }
.kanai-save-project-chosen { display: flex; align-items: center; gap: 8px; font-size: 12.5px; margin-top: 6px; }
.kanai-save-project-chosen button { border: 0; background: transparent; color: var(--kp-ai-ink, #7C3AED); font: inherit; font-size: 12px; cursor: pointer; padding: 0; }
.kanai-save-hint { font-size: 11px; color: var(--kp-text-muted, #7E8FA3); margin-top: 4px; text-align: right; }
.kanai-save-folder {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 10px;
  background: var(--kp-inset, #F0F2F5); font-size: 13px; color: var(--kp-text-body, #475569);
}
.kanai-save-folder svg { width: 14px; height: 14px; color: var(--kp-ai-ink, #7C3AED); flex-shrink: 0; }
.kanai-save-folder strong { font-weight: 600; color: var(--kp-text, #1E293B); }
.kanai-save-preview {
  margin: 0; padding: 10px 12px; border-radius: 10px;
  background: var(--kp-inset, #F0F2F5); border: 1px solid var(--kp-border, #E2E8F0);
  font-family: inherit; font-size: 12.5px; line-height: 1.5; color: var(--kp-text-body, #475569);
  white-space: pre-wrap; word-break: break-word; max-height: 160px; overflow: hidden;
}
.kanai-save-preview-more { font-size: 11px; color: var(--kp-text-muted, #7E8FA3); margin-top: 4px; }
.kanai-save-sources { font-size: 11.5px; color: var(--kp-text-muted, #7E8FA3); }
.kanai-save-error {
  margin-top: 12px; padding: 8px 12px; border-radius: 8px; font-size: 12.5px;
  background: var(--kp-red-light, #FEF2F2); border: 1px solid var(--kp-red-border, #FECACA);
  color: var(--kp-red-ink, #DC2626);
}
.kanai-save-btn[aria-disabled="true"] { opacity: 0.45; cursor: default; }
.kanai-save-btn[aria-disabled="true"]:hover { background: none; color: var(--kp-text-muted, #7E8FA3); }
.kanai-save-saved {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  margin-top: 6px; padding-left: 44px; font-size: 12px; color: var(--kp-text-muted, #7E8FA3);
}
.kanai-save-saved svg { width: 13px; height: 13px; color: var(--kp-ai-ink, #7C3AED); flex-shrink: 0; }
.kanai-save-saved-link {
  background: none; border: none; padding: 0; font: inherit; font-weight: 600;
  color: var(--kp-ai-ink, #7C3AED); cursor: pointer; text-align: left;
}
.kanai-save-saved-link:hover { text-decoration: underline; }
`;

function splitPreview(markdown) {
  const lines = String(markdown || "").split(/\r?\n/).filter((l) => l.trim());
  return { text: lines.slice(0, PREVIEW_LINES).join("\n"), remaining: Math.max(0, lines.length - PREVIEW_LINES) };
}

export default function SaveAnswerModal({
  open, onClose, projectId, defaultTitle, contentMarkdown, question,
  sources, conversationId, modeId, onSaved,
}) {
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  // Tab stannar i dialogen; Esc sköts av lyssnaren nedan (U37).
  useFokusfalla(dialogRef, open, null);

  /* Nollställ och fokusera titeln varje gång modalen öppnas */
  useEffect(() => {
    if (!open) return undefined;
    setTitle(defaultTitle || "");
    setError("");
    setSaving(false);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open, defaultTitle]);

  /* Esc stänger (capture så att inte bakomliggande lyssnare tar tangenten) */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || saving) return;
      e.stopPropagation();
      onClose?.();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, saving, onClose]);

  /* Projektväljare: KanAI-modulen har ingen projektkontext (projektfliken i
     projekten är avstängd sedan 2026-04-30), så användaren väljer projekt här
     när föräldern inte skickar något. */
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [pickedProject, setPickedProject] = useState(null);
  const effectiveProjectId = projectId || pickedProject?.id || null;

  useEffect(() => {
    if (!open || projectId) return undefined;
    let aktiv = true;
    setPickedProject(null);
    setProjectSearch("");
    setProjectsLoading(true);
    setProjectsError("");
    getProjects()
      .then((data) => {
        if (!aktiv) return;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setProjects(list.filter((p) => p && p.id && !p.is_system));
      })
      .catch((err) => { if (aktiv) setProjectsError(err?.message || "Kunde inte hämta projekt"); })
      .finally(() => { if (aktiv) setProjectsLoading(false); });
    return () => { aktiv = false; };
  }, [open, projectId]);

  const visibleProjects = React.useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    const list = q
      ? projects.filter((p) => `${p.number || ""} ${p.name || ""}`.toLowerCase().includes(q))
      : projects;
    return list.slice(0, 40);
  }, [projects, projectSearch]);

  async function handleSave(e) {
    e?.preventDefault();
    const cleanTitle = title.trim();
    if (!cleanTitle || saving) return;
    if (!effectiveProjectId) { setError("Välj projekt först."); return; }
    setSaving(true);
    setError("");
    const payload = {
      project_id: effectiveProjectId,
      title: cleanTitle,
      content_markdown: contentMarkdown || "",
      question: question || "",
      sources: Array.isArray(sources) ? sources : [],
      folder_id: null,
    };
    if (conversationId) payload.conversation_id = conversationId;
    if (modeId) payload.mode_id = modeId;
    try {
      const result = await createArtifact(payload);
      onSaved?.({ ...result, project_id: result?.project_id ?? effectiveProjectId, title: result?.title || cleanTitle });
      onClose?.();
    } catch (err) {
      /* 403 (säkerhetsklassat projekt) m.fl.: backendens detail-text visas som den är */
      setError(err?.message || "Kunde inte spara svaret i projektet");
    } finally {
      setSaving(false);
    }
  }

  const preview = splitPreview(contentMarkdown);
  const sourceCount = Array.isArray(sources) ? sources.length : 0;

  // Portal till body (stagefynd 2026-10-20): inuti meddelandelistan ritades
  // sidans toppmeny och sidfot ovanpå dialogen, så rubrik och knappar doldes.
  return createPortal(
    <>
      <style>{css}</style>
      {open && (
        <div className="kai-modal-overlay kanai-save-overlay" onClick={() => { if (!saving) onClose?.(); }}>
          <form
            className="kai-modal kanai-save-modal"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="kanai-save-modal-title"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSave}
          >
            <div className="kai-modal-header">
              <div className="kai-modal-title" id="kanai-save-modal-title">Spara svaret i projektet</div>
              <button type="button" className="kai-modal-close" onClick={onClose} aria-label="Stäng" disabled={saving}>
                {icons.x}
              </button>
            </div>
            <div className="kai-modal-body">
              {!projectId && (
                <div className="kanai-save-field">
                  <label className="kai-modal-label" htmlFor="kanai-save-project-search">Projekt</label>
                  {pickedProject ? (
                    <div className="kanai-save-project-chosen">
                      {artifactIcons.folder}
                      <span><strong>{pickedProject.number || ""}</strong> {pickedProject.name}</span>
                      <button type="button" onClick={() => setPickedProject(null)}>Byt</button>
                    </div>
                  ) : (
                    <>
                      <input
                        id="kanai-save-project-search"
                        className="kai-modal-input"
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        placeholder="Sök på projektnummer eller namn"
                        autoComplete="off"
                      />
                      <div className="kanai-save-projects" role="listbox" aria-label="Projekt">
                        {projectsLoading && <div className="kanai-save-project-empty">Hämtar projekt…</div>}
                        {!projectsLoading && projectsError && <div className="kanai-save-project-empty">{projectsError}</div>}
                        {!projectsLoading && !projectsError && visibleProjects.length === 0 && (
                          <div className="kanai-save-project-empty">Inga projekt matchar.</div>
                        )}
                        {!projectsLoading && visibleProjects.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            role="option"
                            aria-selected="false"
                            className="kanai-save-project"
                            onClick={() => { setPickedProject(p); setError(""); }}
                          >
                            <span className="kanai-save-project-nr">{p.number || "—"}</span>
                            <span>{p.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="kanai-save-field">
                <label className="kai-modal-label" htmlFor="kanai-save-title-input">Titel</label>
                <input
                  id="kanai-save-title-input"
                  ref={inputRef}
                  className="kai-modal-input"
                  value={title}
                  maxLength={TITLE_MAX_LENGTH}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="T.ex. Sammanfattning av AF-kraven"
                  required
                />
                <div className="kanai-save-hint">{title.length}/{TITLE_MAX_LENGTH}</div>
              </div>
              <div className="kanai-save-field">
                <div className="kanai-save-folder" title="Mappen skapas i projektets dokument om den inte redan finns">
                  {artifactIcons.folder}
                  <span>Mapp: <strong>KanAI-svar</strong></span>
                </div>
              </div>
              <div className="kanai-save-field">
                <span className="kai-modal-label">Innehåll</span>
                <pre className="kanai-save-preview">{preview.text || "(tomt svar)"}</pre>
                {preview.remaining > 0 && (
                  <div className="kanai-save-preview-more">Ytterligare {preview.remaining} rader sparas också.</div>
                )}
              </div>
              <div className="kanai-save-sources">
                {sourceCount > 0
                  ? `${sourceCount} dokumentkäll${sourceCount === 1 ? "a" : "or"} följer med som referenser.`
                  : "Inga dokumentkällor att bifoga."}
              </div>
              {error && <div className="kanai-save-error" role="alert">{error}</div>}
            </div>
            <div className="kai-modal-footer">
              <button type="button" className="kai-modal-btn" onClick={onClose} disabled={saving}>Avbryt</button>
              <button type="submit" className="kai-modal-btn primary" disabled={!title.trim() || saving}>
                {saving ? "Sparar…" : "Spara"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>,
    document.body
  );
}
